import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Player, PlayerMembership, PlayerTelegramAccount } from '../types';
import { apiGet, apiPost, apiPut, apiDelete } from '../utils/api';

type EloBackfillPlayer = {
  playerId: string;
  playerName: string;
  singlesLevel?: string | null;
  suggestedRating: number;
  eloMatchesPlayed: number;
};

type EloBackfillProgress = {
  taskId: string;
  status: 'running' | 'completed' | 'failed';
  startedAt: string;
  finishedAt: string | null;
  recalculate: boolean;
  totalMatches: number;
  handledMatches: number;
  processedMatches: number;
  skippedMatches: number;
  elapsedMs: number;
  etaMs: number | null;
  progressPercent: number;
  errorMessage: string | null;
};

type GlobalEloRecalcStatus = {
  running: EloBackfillProgress | null;
  latestFinished: EloBackfillProgress | null;
};

type PlayerLevelDocumentItem = {
  id: string;
  levelType: 'SINGLES' | 'DOUBLES' | string;
  previousLevel: string | null;
  newLevel: string;
  applyElo?: boolean;
  eloAtEffectiveDate?: number | null;
  sourceType?: string | null;
  sourceMatchAt?: string | null;
  createdAt: string;
};

type PlayerLevelDocument = {
  id: string;
  status: 'DRAFT' | 'POSTED' | string;
  documentType: 'INITIAL_ASSIGNMENT' | 'CHANGE' | string;
  effectiveDate: string;
  postedAt?: string | null;
  notes?: string | null;
  items: PlayerLevelDocumentItem[];
};

type PlayerEloMatchHistoryRow = {
  id: string;
  eventType?: 'MATCH' | 'LEVEL_DOCUMENT' | string;
  discipline: 'SINGLES' | 'DOUBLES' | string;
  ratingBefore?: number | null;
  ratingAfter?: number | null;
  delta?: number | null;
  eventDate?: string | null;
  createdAt: string;
  matchId?: string | null;
  matchDate?: string | null;
  level?: string | null;
  status?: string | null;
  score?: string | null;
  tournamentName?: string | null;
  stage?: string | null;
  opponentName?: string | null;
  opponentRatingBefore?: number | null;
  documentId?: string | null;
  documentType?: string | null;
  levelBefore?: string | null;
  levelAfter?: string | null;
};

type PlayerLevelHistoryRow = {
  date: string;
  effectiveDate: Date;
  singlesLevel: string | null;
  doublesLevel: string | null;
};

type EloChartPoint = {
  id: string;
  rating: number;
  timestamp: number;
  label: string;
  x: number;
  y: number;
  isLevelChange: boolean;
};

type EloLevelGuideLine = {
  level: string;
  rating: number;
  y: number;
};

const ELO_CHART_MIN_RATING = 1000;
const ELO_CHART_MAX_RATING = 3000;

const SINGLES_ELO_START_LEGEND: Array<{ level: string; rating: number }> = [
  { level: 'S-4F', rating: 1200 },
  { level: 'S-4M', rating: 1500 },
  { level: 'S-5', rating: 1800 },
  { level: 'S-6', rating: 2100 },
  { level: 'S-7', rating: 2400 },
  { level: 'S-8', rating: 2700 },
  { level: 'S-9', rating: 3000 }
];

const Players: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const hasMountedRef = useRef(false);
  const getSearchFromParams = (params: URLSearchParams): string => {
    const explicitSearch = String(params.get('search') || '').trim();
    if (explicitSearch) return explicitSearch;
    const playerIdSearch = String(params.get('playerId') || '').trim();
    return playerIdSearch;
  };
  const getPageFromParams = (params: URLSearchParams): number => {
    const raw = Number(params.get('page') || 1);
    return Number.isFinite(raw) && raw > 0 ? Math.floor(raw) : 1;
  };
  const setPageInUrl = (page: number) => {
    const nextParams = new URLSearchParams(searchParams);
    if (page <= 1) {
      nextParams.delete('page');
    } else {
      nextParams.set('page', String(page));
    }
    setSearchParams(nextParams);
  };
  const [players, setPlayers] = useState<Player[]>([]);
  const [loading, setLoading] = useState(true);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingPlayer, setEditingPlayer] = useState<Player | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showDeactivateConfirm, setShowDeactivateConfirm] = useState(false);
  const [playerToDelete, setPlayerToDelete] = useState<Player | null>(null);
  const [pagination, setPagination] = useState({
    page: getPageFromParams(searchParams),
    limit: 12,
    total: 0,
    pages: 0
  });
  const [filters, setFilters] = useState({
    level: '',
    search: getSearchFromParams(searchParams),
    isActive: 'true'
  });
  const [sortBy, setSortBy] = useState<'name_asc' | 'elo_desc'>('name_asc');
  
  // Отдельное состояние для поиска (для debounce)
  const [searchInput, setSearchInput] = useState(getSearchFromParams(searchParams));
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  
  // Состояние для запоминания последних выбранных уровней
  const [lastSelectedSinglesLevel, setLastSelectedSinglesLevel] = useState<string>('S-6');
  const [lastSelectedDoublesLevel, setLastSelectedDoublesLevel] = useState<string>('D-6');
  const [showEloBackfillModal, setShowEloBackfillModal] = useState(false);
  const [eloBackfillPlayers, setEloBackfillPlayers] = useState<EloBackfillPlayer[]>([]);
  const [eloBackfillRatings, setEloBackfillRatings] = useState<Record<string, string>>({});
  const [loadingEloBackfill, setLoadingEloBackfill] = useState(false);
  const [savingEloBackfill, setSavingEloBackfill] = useState(false);
  const [recalculateEloFrom2022, setRecalculateEloFrom2022] = useState(true);
  const [eloKFactor, setEloKFactor] = useState('32');
  const [eloBackfillProgress, setEloBackfillProgress] = useState<EloBackfillProgress | null>(null);
  const [globalEloRecalcStatus, setGlobalEloRecalcStatus] = useState<GlobalEloRecalcStatus>({
    running: null,
    latestFinished: null
  });
  const [startingGlobalEloRecalc, setStartingGlobalEloRecalc] = useState(false);
  const [showLevelDocsModal, setShowLevelDocsModal] = useState(false);
  const [levelDocsLoading, setLevelDocsLoading] = useState(false);
  const [levelDocsRefreshing, setLevelDocsRefreshing] = useState(false);
  const [levelDocsPlayerId, setLevelDocsPlayerId] = useState('');
  const [levelDocsPlayerName, setLevelDocsPlayerName] = useState('');
  const [levelDocs, setLevelDocs] = useState<PlayerLevelDocument[]>([]);
  const [levelDocsTab, setLevelDocsTab] = useState<'levels' | 'elo'>('levels');
  const [eloMatchHistory, setEloMatchHistory] = useState<PlayerEloMatchHistoryRow[]>([]);
  const [eloChartDiscipline, setEloChartDiscipline] = useState<'SINGLES' | 'DOUBLES'>('SINGLES');
  const [eloHoveredPointId, setEloHoveredPointId] = useState<string | null>(null);
  const [levelEloRecalculating, setLevelEloRecalculating] = useState(false);
  const [levelDocActionLoadingId, setLevelDocActionLoadingId] = useState('');
  const [showLevelHistoryModal, setShowLevelHistoryModal] = useState(false);
  const [levelHistoryLoading, setLevelHistoryLoading] = useState(false);
  const [levelHistoryPlayerName, setLevelHistoryPlayerName] = useState('');
  const [levelHistoryRows, setLevelHistoryRows] = useState<PlayerLevelHistoryRow[]>([]);
  const singlesLevelGuideLegend = useMemo(
    () =>
      SINGLES_ELO_START_LEGEND.filter((item) =>
        ['S-4F', 'S-4M', 'S-5', 'S-6', 'S-7', 'S-8'].includes(item.level)
      ),
    []
  );
  const eloChartData = useMemo(() => {
    const rows = (eloMatchHistory || [])
      .filter((row) => String(row.discipline || '').toUpperCase() === eloChartDiscipline)
      .map((row) => {
        if (typeof row.ratingAfter !== 'number' || !Number.isFinite(row.ratingAfter)) {
          return null;
        }
        const rawDate = row.eventDate || row.matchDate || row.createdAt;
        const timestamp = new Date(rawDate).getTime();
        if (!Number.isFinite(timestamp)) return null;
        return {
          id: row.id,
          rating: row.ratingAfter,
          timestamp,
          label: new Date(rawDate).toLocaleDateString('ru-RU'),
          isLevelChange: String(row.eventType || '').toUpperCase() === 'LEVEL_DOCUMENT'
        };
      })
      .filter((item): item is { id: string; rating: number; timestamp: number; label: string; isLevelChange: boolean } => Boolean(item))
      .sort((a, b) => a.timestamp - b.timestamp);

    if (rows.length < 1) return null;

    const width = 1000;
    const height = 440;
    const padLeft = 40;
    const padRight = 4;
    const padTop = 16;
    const padBottom = 30;
    const rightLabelGutter = 98;
    const plotRight = width - padRight - rightLabelGutter;
    const innerW = plotRight - padLeft;
    const innerH = height - padTop - padBottom;

    const minRating = Math.min(...rows.map((row) => row.rating));
    const maxRating = Math.max(...rows.map((row) => row.rating));
    const guideLevels = eloChartDiscipline === 'SINGLES' ? singlesLevelGuideLegend : [];
    const minScaleRating = ELO_CHART_MIN_RATING;
    const maxScaleRating = ELO_CHART_MAX_RATING;
    const range = Math.max(1, maxScaleRating - minScaleRating);

    const points: EloChartPoint[] = rows.map((row, index) => {
      const ratioX = rows.length === 1 ? 0 : index / (rows.length - 1);
      const x = padLeft + ratioX * innerW;
      const y = padTop + ((maxScaleRating - row.rating) / range) * innerH;
      return { ...row, x, y };
    });

    const levelGuides: EloLevelGuideLine[] = guideLevels.map((item) => ({
      level: item.level,
      rating: item.rating,
      y: padTop + ((maxScaleRating - item.rating) / range) * innerH
    }));

    const linePath = points
      .map((point, index) => `${index === 0 ? 'M' : 'L'} ${point.x.toFixed(2)} ${point.y.toFixed(2)}`)
      .join(' ');

    const currentPoint = points[points.length - 1];

    return {
      width,
      height,
      padLeft,
      padRight,
      padTop,
      padBottom,
      plotRight,
      minRating,
      maxRating,
      minScaleRating,
      maxScaleRating,
      linePath,
      points,
      levelGuides,
      firstLabel: points[0]?.label || '',
      lastLabel: points[points.length - 1]?.label || '',
      currentRating: currentPoint?.rating ?? null
    };
  }, [eloMatchHistory, eloChartDiscipline, singlesLevelGuideLegend]);

  useEffect(() => {
    const hasSingles = eloMatchHistory.some(
      (row) => String(row.discipline || '').toUpperCase() === 'SINGLES' && typeof row.ratingAfter === 'number'
    );
    const hasDoubles = eloMatchHistory.some(
      (row) => String(row.discipline || '').toUpperCase() === 'DOUBLES' && typeof row.ratingAfter === 'number'
    );
    if (eloChartDiscipline === 'SINGLES' && !hasSingles && hasDoubles) {
      setEloChartDiscipline('DOUBLES');
    } else if (eloChartDiscipline === 'DOUBLES' && !hasDoubles && hasSingles) {
      setEloChartDiscipline('SINGLES');
    }
  }, [eloMatchHistory, eloChartDiscipline]);

  useEffect(() => {
    setEloHoveredPointId(null);
  }, [eloChartDiscipline, showLevelDocsModal]);

  const filteredEloHistoryRows = useMemo(
    () =>
      (eloMatchHistory || []).filter(
        (row) => String(row.discipline || '').toUpperCase() === eloChartDiscipline
      ),
    [eloMatchHistory, eloChartDiscipline]
  );

  const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

  const formatDuration = (ms?: number | null): string => {
    const safeMs = Math.max(0, Number(ms || 0));
    const totalSeconds = Math.floor(safeMs / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    if (hours > 0) {
      return `${hours}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
    }
    return `${minutes}:${String(seconds).padStart(2, '0')}`;
  };

  const singlesLevels = [
    { value: '', label: 'Все уровни одиночки' },
    { value: 'S-4F', label: 'S-4F (4-й женский)' },
    { value: 'S-4M', label: 'S-4M (4-й мужской)' },
    { value: 'S-5', label: 'S-5 (5-й)' },
    { value: 'S-6', label: 'S-6 (6-й)' },
    { value: 'S-7', label: 'S-7 (7-й)' },
    { value: 'S-8', label: 'S-8 (8-й)' },
    { value: 'S-9', label: 'S-9 (9-й)' }
  ];

  const doublesLevels = [
    { value: '', label: 'Все уровни пар' },
    { value: 'D-Mixt', label: 'D-Mixt (Смешанная)' },
    { value: 'D-6', label: 'D-6 (6-й)' },
    { value: 'D-6.5', label: 'D-6.5 (6.5-й)' },
    { value: 'D-7', label: 'D-7 (7-й)' },
    { value: 'D-ProAm', label: 'D-ProAm (Про-Ам)' }
  ];

  const allLevels = [
    { value: '', label: 'Все уровни' },
    ...singlesLevels.slice(1),
    ...doublesLevels.slice(1)
  ];

  const getLevelLabel = (level: string) => {
    const levelObj = allLevels.find(l => l.value === level);
    const fullLabel = levelObj ? levelObj.label : level;
    return String(fullLabel || '').split(' ')[0] || level;
  };

  const getLevelColor = (level: string) => {
    const colors = {
      'S-4F': 'bg-pink-100 text-pink-800',
      'S-4M': 'bg-blue-100 text-blue-800',
      'S-5': 'bg-green-100 text-green-800',
      'S-6': 'bg-yellow-100 text-yellow-800',
      'S-7': 'bg-orange-100 text-orange-800',
      'S-8': 'bg-red-100 text-red-800',
      'S-9': 'bg-fuchsia-100 text-fuchsia-800',
      'D-Mixt': 'bg-purple-100 text-purple-800',
      'D-6': 'bg-indigo-100 text-indigo-800',
      'D-6.5': 'bg-violet-100 text-violet-800',
      'D-7': 'bg-teal-100 text-teal-800',
      'D-ProAm': 'bg-amber-100 text-amber-800'
    };
    return colors[level as keyof typeof colors] || 'bg-gray-100 text-gray-800';
  };

  // Debounce для поиска
  useEffect(() => {
    // Очищаем предыдущий таймер
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    // Устанавливаем новый таймер
    searchTimeoutRef.current = setTimeout(() => {
      setFilters(prev => {
        if (prev.search === searchInput) return prev;
        return { ...prev, search: searchInput };
      });
    }, 1000); // Задержка 1000мс
    
    // Очистка при размонтировании
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchInput]);

  // Синхронизация searchInput с filters.search при монтировании
  useEffect(() => {
    setSearchInput(filters.search);
  }, []); // Только при монтировании

  useEffect(() => {
    // Загружаем сохраненные значения из localStorage
    const savedSinglesLevel = localStorage.getItem('lastSelectedPlayerSinglesLevel') || 'S-6';
    const savedDoublesLevel = localStorage.getItem('lastSelectedPlayerDoublesLevel') || 'D-6';
    setLastSelectedSinglesLevel(savedSinglesLevel);
    setLastSelectedDoublesLevel(savedDoublesLevel);
  }, []);

  useEffect(() => {
    const currentPage = getPageFromParams(searchParams);
    fetchPlayers(currentPage);
  }, [searchParams]);

  useEffect(() => {
    if (!hasMountedRef.current) {
      hasMountedRef.current = true;
      return;
    }
    const currentPage = getPageFromParams(searchParams);
    if (currentPage !== 1) {
      setPagination(prev => ({ ...prev, page: 1 }));
      setPageInUrl(1);
      return;
    }
    fetchPlayers(1);
  }, [filters, sortBy]);

  const fetchPlayers = async (page = pagination.page) => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filters.level) params.append('level', filters.level);
      if (filters.search) params.append('search', filters.search);
      if (filters.isActive) params.append('isActive', filters.isActive);
      params.append('page', page.toString());
      params.append('limit', pagination.limit.toString());
      if (sortBy === 'elo_desc') {
        params.append('sortBy', 'elo');
        params.append('sortOrder', 'desc');
      } else {
        params.append('sortBy', 'name');
        params.append('sortOrder', 'asc');
      }

      const data = await apiGet<{ players: Player[]; pagination: any }>(`/players?${params}`);
      setPlayers(data.players || []);
      setPagination(prev => ({
        ...prev,
        page: data.pagination?.page || page,
        total: data.pagination?.total || 0,
        pages: data.pagination?.pages || 0
      }));
    } catch (error) {
      console.error('Error fetching players:', error);
    } finally {
      setLoading(false);
      setHasLoadedOnce(true);
    }
  };

  const handleCreate = () => {
    setEditingPlayer(null);
    setShowForm(true);
  };

  const handleEdit = (player: Player) => {
    setEditingPlayer(player);
    setShowForm(true);
  };

  const handleFormSave = async (playerData: Partial<Player>) => {
    try {
      let savedPlayer: Player;
      if (editingPlayer) {
        const updated = await apiPut<Player>(`/players/${editingPlayer.id}`, playerData);
        savedPlayer = updated;
        // Обновляем editingPlayer, чтобы форма отобразила изменения
        // ВАЖНО: Обновляем editingPlayer с сохраненными данными, включая очищенные поля
        setEditingPlayer({ ...editingPlayer, ...savedPlayer });
        // Закрываем форму после успешного сохранения
        setShowForm(false);
        setEditingPlayer(null);
        fetchPlayers();
      } else {
        savedPlayer = await apiPost<Player>('/players', playerData);
        // Запоминаем последние выбранные уровни при создании нового игрока
        if (playerData.singlesLevel) {
          localStorage.setItem('lastSelectedPlayerSinglesLevel', playerData.singlesLevel);
          setLastSelectedSinglesLevel(playerData.singlesLevel);
        }
        if (playerData.doublesLevel) {
          localStorage.setItem('lastSelectedPlayerDoublesLevel', playerData.doublesLevel);
          setLastSelectedDoublesLevel(playerData.doublesLevel);
        }
        // При создании нового игрока закрываем форму
        setShowForm(false);
        setEditingPlayer(null);
        fetchPlayers();
      }
    } catch (error: any) {
      console.error('Error saving player:', error);
      alert(error.message || 'Произошла ошибка при сохранении игрока');
    }
  };

  const handleFormCancel = () => {
    setShowForm(false);
    setEditingPlayer(null);
  };

  const handleDeleteClick = (player: Player) => {
    setPlayerToDelete(player);
    setShowDeleteConfirm(true);
  };

  const handleDeactivateClick = (player: Player) => {
    setPlayerToDelete(player);
    setShowDeactivateConfirm(true);
  };

  const handleDeleteConfirm = async () => {
    if (!playerToDelete) return;

    try {
      await apiDelete(`/players/${playerToDelete.id}/force`);
      setShowDeleteConfirm(false);
      setPlayerToDelete(null);
      fetchPlayers();
      alert('Игрок успешно удален');
    } catch (error: any) {
      console.error('Error deleting player:', error);
      alert(error.message || 'Произошла ошибка при удалении игрока');
    }
  };

  const handleDeactivateConfirm = async () => {
    if (!playerToDelete) return;

    try {
      await apiDelete(`/players/${playerToDelete.id}`);
      setShowDeactivateConfirm(false);
      setPlayerToDelete(null);
      fetchPlayers();
      alert('Игрок успешно деактивирован');
    } catch (error: any) {
      console.error('Error deactivating player:', error);
      alert(error.message || 'Произошла ошибка при деактивации игрока');
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteConfirm(false);
    setPlayerToDelete(null);
  };

  const handleDeactivateCancel = () => {
    setShowDeactivateConfirm(false);
    setPlayerToDelete(null);
  };

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= pagination.pages) {
      setPageInUrl(newPage);
    }
  };

  const loadPlayerLevelDocuments = async (playerId: string, options?: { isRefresh?: boolean }) => {
    const isRefresh = options?.isRefresh === true;
    if (!playerId) return;
    try {
      if (isRefresh) setLevelDocsRefreshing(true);
      else setLevelDocsLoading(true);
      const data = await apiGet<{
        documents: PlayerLevelDocument[];
        eloMatches?: PlayerEloMatchHistoryRow[];
      }>(`/players/${playerId}/level-change-documents`);
      setLevelDocs(data.documents || []);
      setEloMatchHistory(data.eloMatches || []);
    } finally {
      if (isRefresh) setLevelDocsRefreshing(false);
      else setLevelDocsLoading(false);
    }
  };

  const openPlayerLevelDocuments = async (player: Player) => {
    try {
      setShowLevelDocsModal(true);
      setLevelDocsPlayerId(player.id);
      setLevelDocsPlayerName(player.name || 'Игрок');
      setLevelDocsTab('levels');
      await loadPlayerLevelDocuments(player.id);
    } catch (error: any) {
      console.error('Error loading player level documents:', error);
      alert(error.message || 'Не удалось загрузить документы игрока');
      setShowLevelDocsModal(false);
    }
  };

  const openCreateLevelDocument = () => {
    if (!levelDocsPlayerId) return;
    const url = `/documents/player-level-changes?mode=create&playerId=${encodeURIComponent(levelDocsPlayerId)}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const recalculateSelectedPlayerElo = async () => {
    if (!levelDocsPlayerId) return;
    const confirmed = window.confirm('Пересчитать Elo только для этого игрока?');
    if (!confirmed) return;
    try {
      setLevelEloRecalculating(true);
      await apiPost(`/players/${levelDocsPlayerId}/recalculate-elo`, {});
      await loadPlayerLevelDocuments(levelDocsPlayerId, { isRefresh: true });
    } catch (error: any) {
      console.error('Error recalculating player elo:', error);
      alert(error.message || 'Не удалось пересчитать Elo игрока');
    } finally {
      setLevelEloRecalculating(false);
    }
  };

  const handleLevelDocStatusChange = async (documentId: string, nextStatus: 'DRAFT' | 'POSTED') => {
    const target = levelDocs.find((doc) => doc.id === documentId);
    if (!target) return;
    const isRepost = nextStatus === 'POSTED' && String(target.status || '').toUpperCase() === 'POSTED';
    const actionText = nextStatus === 'POSTED' ? (isRepost ? 'перепровести' : 'провести') : 'снять с проведения';
    const confirmed = window.confirm(
      `Подтвердите действие: ${actionText} документ от ${new Date(target.effectiveDate).toLocaleDateString('ru-RU')}?`
    );
    if (!confirmed) return;

    try {
      setLevelDocActionLoadingId(documentId);
      const updated = await apiPut<PlayerLevelDocument>(`/player-level-change-documents/${documentId}/status`, {
        status: nextStatus
      });
      setLevelDocs((prev) =>
        prev.map((doc) =>
          doc.id === documentId
            ? { ...doc, status: updated.status, postedAt: updated.postedAt || null }
            : doc
        )
      );
    } catch (error: any) {
      console.error('Error updating level-change document status:', error);
      alert(error.message || 'Не удалось изменить статус документа');
    } finally {
      setLevelDocActionLoadingId('');
    }
  };

  const handleLevelDocDelete = async (documentId: string) => {
    const target = levelDocs.find((doc) => doc.id === documentId);
    if (!target) return;
    if (String(target.status || '').toUpperCase() !== 'DRAFT') {
      alert('Удаление доступно только для документов в статусе Черновик.');
      return;
    }
    const confirmed = window.confirm('Удалить документ?');
    if (!confirmed) return;

    try {
      setLevelDocActionLoadingId(documentId);
      await apiDelete(`/player-level-change-documents/${documentId}`);
      setLevelDocs((prev) => prev.filter((doc) => doc.id !== documentId));
    } catch (error: any) {
      console.error('Error deleting level-change document:', error);
      alert(error.message || 'Не удалось удалить документ');
    } finally {
      setLevelDocActionLoadingId('');
    }
  };

  const buildLevelHistoryRows = (documents: PlayerLevelDocument[]): PlayerLevelHistoryRow[] => {
    const postedDocs = (documents || [])
      .filter((doc) => String(doc.status || '').toUpperCase() === 'POSTED')
      .sort((a, b) => {
        const aTime = new Date(a.effectiveDate).getTime();
        const bTime = new Date(b.effectiveDate).getTime();
        if (aTime !== bTime) return aTime - bTime;
        return new Date(a.postedAt || a.effectiveDate).getTime() - new Date(b.postedAt || b.effectiveDate).getTime();
      });

    const rowsByDate = new Map<string, PlayerLevelHistoryRow>();
    let currentSingles: string | null = null;
    let currentDoubles: string | null = null;

    for (const doc of postedDocs) {
      const effectiveDateObj = new Date(doc.effectiveDate);
      const dateKey = effectiveDateObj.toISOString().split('T')[0];
      let changed = false;
      let singlesChanged = false;
      let doublesChanged = false;

      for (const item of doc.items || []) {
        const levelType = String(item.levelType || '').toUpperCase();
        const nextLevel = item.newLevel || null;
        if (!nextLevel) continue;

        if (levelType === 'SINGLES' && currentSingles !== nextLevel) {
          currentSingles = nextLevel;
          changed = true;
          singlesChanged = true;
        } else if (levelType === 'DOUBLES' && currentDoubles !== nextLevel) {
          currentDoubles = nextLevel;
          changed = true;
          doublesChanged = true;
        }
      }

      if (!changed) continue;

      const existingRow = rowsByDate.get(dateKey);
      const nextRow: PlayerLevelHistoryRow = existingRow || {
        date: effectiveDateObj.toLocaleDateString('ru-RU'),
        effectiveDate: effectiveDateObj,
        singlesLevel: null,
        doublesLevel: null
      };

      for (const item of doc.items || []) {
        const levelType = String(item.levelType || '').toUpperCase();
        const nextLevel = item.newLevel || null;
        if (!nextLevel) continue;
        if (levelType === 'SINGLES' && singlesChanged) {
          nextRow.singlesLevel = nextLevel;
        } else if (levelType === 'DOUBLES' && doublesChanged) {
          nextRow.doublesLevel = nextLevel;
        }
      }

      rowsByDate.set(dateKey, nextRow);
    }

    return Array.from(rowsByDate.values()).sort((a, b) => b.effectiveDate.getTime() - a.effectiveDate.getTime());
  };

  const openPlayerLevelHistory = async (player: Player) => {
    try {
      setShowLevelHistoryModal(true);
      setLevelHistoryLoading(true);
      setLevelHistoryPlayerName(player.name || 'Игрок');
      const data = await apiGet<{ documents: PlayerLevelDocument[] }>(`/players/${player.id}/level-change-documents`);
      setLevelHistoryRows(buildLevelHistoryRows(data.documents || []));
    } catch (error: any) {
      console.error('Error loading player level history:', error);
      alert(error.message || 'Не удалось загрузить историю уровней игрока');
      setShowLevelHistoryModal(false);
    } finally {
      setLevelHistoryLoading(false);
    }
  };

  const openEloBackfillModal = async () => {
    try {
      setLoadingEloBackfill(true);
      setShowEloBackfillModal(true);
      setEloBackfillProgress(null);
      const data = await apiGet<{ players: EloBackfillPlayer[] }>(
        '/elo/backfill/players?asOfDate=2022-01-01'
      );
      const playersForBackfill = data.players || [];
      setEloBackfillPlayers(playersForBackfill);
      const ratingsMap: Record<string, string> = {};
      playersForBackfill.forEach((item) => {
        ratingsMap[item.playerId] = String(item.suggestedRating || '');
      });
      setEloBackfillRatings(ratingsMap);
    } catch (error: any) {
      console.error('Error loading elo backfill players:', error);
      alert(error.message || 'Не удалось загрузить список для начальных Elo остатков');
      setShowEloBackfillModal(false);
    } finally {
      setLoadingEloBackfill(false);
    }
  };

  const loadGlobalEloRecalcStatus = async () => {
    try {
      const data = await apiGet<GlobalEloRecalcStatus>('/elo/recalculate-all/status');
      setGlobalEloRecalcStatus({
        running: data?.running || null,
        latestFinished: data?.latestFinished || null
      });
    } catch (error) {
      console.error('Error loading global elo recalculation status:', error);
    }
  };

  const startGlobalEloRecalculation = async () => {
    const confirmed = window.confirm('Запустить пересчет Elo для всех игроков за весь период?');
    if (!confirmed) return;
    try {
      setStartingGlobalEloRecalc(true);
      await apiPost('/elo/recalculate-all/start', {});
      await loadGlobalEloRecalcStatus();
      alert('Пересчет запущен в фоне на сервере.');
    } catch (error: any) {
      console.error('Error starting global elo recalculation:', error);
      alert(error.message || 'Не удалось запустить пересчет Elo');
    } finally {
      setStartingGlobalEloRecalc(false);
    }
  };

  useEffect(() => {
    loadGlobalEloRecalcStatus();
  }, []);

  useEffect(() => {
    if (!globalEloRecalcStatus.running) return;
    const timer = setInterval(() => {
      loadGlobalEloRecalcStatus();
    }, 5000);
    return () => clearInterval(timer);
  }, [globalEloRecalcStatus.running?.taskId]);

  const handleEloBackfillSave = async () => {
    try {
      setSavingEloBackfill(true);
      setEloBackfillProgress(null);
      const items = eloBackfillPlayers
        .map((player) => ({
          playerId: player.playerId,
          rating: Number(eloBackfillRatings[player.playerId] || 0)
        }))
        .filter((item) => Number.isFinite(item.rating) && item.rating > 0);

      if (items.length < 1) {
        alert('Укажите хотя бы один корректный Elo рейтинг');
        return;
      }

      const startResponse = await apiPost<{ success: boolean; taskId: string }>(
        '/elo/backfill/initial-balances/start',
        {
          asOfDate: '2022-01-01',
          discipline: 'SINGLES',
          recalculate: recalculateEloFrom2022,
          kFactor: Number(eloKFactor || 32),
          items
        }
      );
      const taskId = String(startResponse?.taskId || '').trim();
      if (!taskId) {
        throw new Error('Не удалось получить идентификатор задачи пересчета');
      }

      let finalProgress: EloBackfillProgress | null = null;
      for (let i = 0; i < 7200; i += 1) {
        const progress = await apiGet<EloBackfillProgress>(`/elo/backfill/initial-balances/progress/${taskId}`);
        setEloBackfillProgress(progress);
        finalProgress = progress;
        if (progress.status === 'completed' || progress.status === 'failed') {
          break;
        }
        await sleep(1000);
      }

      if (!finalProgress) {
        throw new Error('Не удалось получить прогресс пересчета Elo');
      }
      if (finalProgress.status === 'failed') {
        throw new Error(finalProgress.errorMessage || 'Ошибка пересчета Elo');
      }

      alert(
        recalculateEloFrom2022
          ? `Начальные остатки сохранены. Пересчет завершен: обработано ${finalProgress.processedMatches || 0} из ${finalProgress.totalMatches || 0}, пропущено ${finalProgress.skippedMatches || 0}.`
          : 'Начальные Elo остатки сохранены.'
      );
      setShowEloBackfillModal(false);
      setEloBackfillProgress(null);
      fetchPlayers();
    } catch (error: any) {
      console.error('Error saving elo backfill:', error);
      alert(error.message || 'Ошибка сохранения начальных Elo остатков');
    } finally {
      setSavingEloBackfill(false);
    }
  };

  if (loading && !hasLoadedOnce) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold text-gray-900">Игроки</h1>
        <div className="flex flex-col items-end gap-1">
          <div className="flex gap-2">
            <button
              onClick={startGlobalEloRecalculation}
              disabled={startingGlobalEloRecalc || Boolean(globalEloRecalcStatus.running)}
              className="bg-emerald-700 text-white px-4 py-2 rounded-lg hover:bg-emerald-800 transition-colors disabled:opacity-50"
            >
              {globalEloRecalcStatus.running ? 'Пересчет Elo выполняется...' : 'Пересчет Elo (все игроки)'}
            </button>
            <button
              onClick={openEloBackfillModal}
              className="bg-gray-700 text-white px-4 py-2 rounded-lg hover:bg-gray-800 transition-colors"
            >
              Начальные Elo (01.01.2022)
            </button>
            <button
              onClick={handleCreate}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
            >
              Добавить игрока
            </button>
          </div>
          <div className="text-xs text-gray-500">
            {globalEloRecalcStatus.latestFinished?.finishedAt
              ? `Последний пересчет: ${new Date(globalEloRecalcStatus.latestFinished.finishedAt).toLocaleString('ru-RU')} (${formatDuration(globalEloRecalcStatus.latestFinished.elapsedMs)})`
              : 'Последний пересчет: —'}
          </div>
        </div>
      </div>

      {/* Фильтры */}
      <div className="bg-white p-4 rounded-lg shadow">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Уровень
            </label>
            <select
              value={filters.level}
              onChange={(e) => setFilters({ ...filters, level: e.target.value })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {allLevels.map(level => (
                <option key={level.value} value={level.value}>
                  {level.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Поиск
            </label>
            <div className="relative">
              <input
                type="text"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Имя, email или телефон"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 pr-8 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              {searchInput && (
                <button
                  type="button"
                  onClick={() => setSearchInput('')}
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 focus:outline-none"
                  aria-label="Очистить поиск"
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              )}
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Статус
            </label>
            <select
              value={filters.isActive}
              onChange={(e) => setFilters({ ...filters, isActive: e.target.value })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="true">Активные</option>
              <option value="false">Неактивные</option>
              <option value="">Все</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Сортировка
            </label>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as 'name_asc' | 'elo_desc')}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="name_asc">По имени (А-Я)</option>
              <option value="elo_desc">По Elo (по убыванию)</option>
            </select>
          </div>
        </div>
      </div>

      {/* Список игроков */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {players.map((player) => {
          const singlesElo = player.eloRatings?.find((rating) => rating.discipline === 'SINGLES');
          const doublesElo = player.eloRatings?.find((rating) => rating.discipline === 'DOUBLES');
          const hasUnpostedDocuments = Boolean(
            player.hasUnpostedDocuments || (player.unpostedDocumentsCount || 0) > 0
          );
          const hasNoDocuments = (player.documentsCount || 0) === 0;
          const hasUnknownSinglesLevel = String(player.singlesLevel || '').trim().toUpperCase() === 'UNKNOWN';
          const hasUnknownDoublesLevel = String(player.doublesLevel || '').trim().toUpperCase() === 'UNKNOWN';

          return (
          <div key={player.id} className="bg-white rounded-lg shadow p-6">
            <div className="flex justify-between items-start mb-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">{player.name}</h3>
                {player.email && (
                  <p className="text-sm text-gray-600">{player.email}</p>
                )}
                {player.phone && (
                  <p className="text-sm text-gray-600">{player.phone}</p>
                )}
                {player.registrationDate && (
                  <p className="text-sm text-gray-600">
                    Дата регистрации:{' '}
                    <span className="text-gray-800">
                      {new Date(player.registrationDate).toLocaleDateString('ru-RU')}
                    </span>
                  </p>
                )}
                {player.firstSinglesMatchDate && (
                  <p className="text-sm text-gray-600">
                    Первый матч (одиночка):{' '}
                    <span className="text-gray-800">
                      {new Date(player.firstSinglesMatchDate).toLocaleDateString('ru-RU')}
                    </span>
                  </p>
                )}
                {player.firstDoublesMatchDate && (
                  <p className="text-sm text-gray-600">
                    Первый матч (пара):{' '}
                    <span className="text-gray-800">
                      {new Date(player.firstDoublesMatchDate).toLocaleDateString('ru-RU')}
                    </span>
                  </p>
                )}
                <div className="mt-2 p-2 rounded border border-slate-200 bg-slate-50">
                  <p className="text-xs font-semibold text-slate-700">TTM PLAY</p>
                  <p className="text-xs text-slate-600">
                    Тестер TTM PLAY:{' '}
                    <span className="font-medium text-slate-800">
                      {player.playTestCohort?.enabled ? 'да' : 'нет'}
                    </span>
                  </p>
                  <p className="text-xs text-slate-600">
                    Срок доступа:{' '}
                    <span className="text-slate-800">
                      {player.playTestCohort?.expiresAt ? new Date(player.playTestCohort.expiresAt).toLocaleString('ru-RU') : '—'}
                    </span>
                  </p>
                  <p className="text-xs text-slate-600">
                    Комментарий:{' '}
                    <span className="text-slate-800">{player.playTestCohort?.note || '—'}</span>
                  </p>
                </div>
                {/* Старый формат (telegramChatId и telegramUsername из таблицы Player) */}
                {(player.telegramChatId || player.telegramUsername) && (
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-sm text-gray-500">Telegram:</span>
                    {player.telegramChatId && (
                      <span className="text-sm text-gray-600">Chat ID: {player.telegramChatId}</span>
                    )}
                    {player.telegramUsername && (
                      <span className="text-sm text-blue-600">{player.telegramUsername}</span>
                    )}
                  </div>
                )}
                {/* Новый формат (PlayerTelegramAccount) */}
                {player.playerTelegramAccounts && player.playerTelegramAccounts.length > 0 && (
                  <div className="mt-1 space-y-1">
                    {player.playerTelegramAccounts.map((account) => (
                      <div key={account.id} className="flex items-center gap-2">
                        <span className="text-sm text-gray-500">Telegram:</span>
                        <span className="text-sm text-gray-600">Chat ID: {account.chatId}</span>
                        {account.username && (
                          <span className="text-sm text-blue-600">@{account.username}</span>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="mb-4">
              <div className="flex flex-wrap gap-2">
                {player.singlesLevel && typeof player.singlesLevel === 'string' && player.singlesLevel.trim() !== '' && (
                  <div className="flex items-center">
                    <span className="text-sm text-gray-600 mr-2">Одиночка:</span>
                    <button
                      type="button"
                      onClick={() => openPlayerLevelHistory(player)}
                      className={`px-2 py-1 rounded-full text-xs font-medium hover:opacity-90 hover:ring-1 hover:ring-blue-400 transition ${getLevelColor(player.singlesLevel)} ${hasNoDocuments || hasUnknownSinglesLevel ? 'border border-red-500' : ''}`}
                      title="Открыть историю уровней"
                    >
                      {getLevelLabel(player.singlesLevel)}
                    </button>
                  </div>
                )}
                {player.doublesLevel && typeof player.doublesLevel === 'string' && player.doublesLevel.trim() !== '' && (
                  <div className="flex items-center">
                    <span className="text-sm text-gray-600 mr-2">Пара:</span>
                    <button
                      type="button"
                      onClick={() => openPlayerLevelHistory(player)}
                      className={`px-2 py-1 rounded-full text-xs font-medium hover:opacity-90 hover:ring-1 hover:ring-blue-400 transition ${getLevelColor(player.doublesLevel)} ${hasNoDocuments || hasUnknownDoublesLevel ? 'border border-red-500' : ''}`}
                      title="Открыть историю уровней"
                    >
                      {getLevelLabel(player.doublesLevel)}
                    </button>
                  </div>
                )}
                {(!player.singlesLevel || (typeof player.singlesLevel === 'string' && player.singlesLevel.trim() === '')) && 
                 (!player.doublesLevel || (typeof player.doublesLevel === 'string' && player.doublesLevel.trim() === '')) && (
                  <span className="text-sm text-gray-500">Уровни не указаны</span>
                )}
              </div>
            </div>

            <div className="mb-4 space-y-1 text-sm">
              <div className="text-gray-700">
                Elo одиночка:{' '}
                <span className={`font-medium ${
                  player.singlesEloApplyDisabled
                    ? 'bg-yellow-100 text-red-700 px-1.5 py-0.5 rounded'
                    : 'text-gray-900'
                }`}>
                  {singlesElo ? `${singlesElo.currentRating}` : '—'}
                </span>
                {singlesElo ? (
                  <span className={`ml-2 ${
                    singlesElo.lastDelta > 0 ? 'text-green-600' : singlesElo.lastDelta < 0 ? 'text-red-600' : 'text-gray-900'
                  }`}>
                    {singlesElo.lastDelta >= 0 ? '+' : ''}
                    {singlesElo.lastDelta}
                  </span>
                ) : null}
                {singlesElo ? (
                  <span className="ml-2 text-gray-500">игр: {singlesElo.matchesPlayed}</span>
                ) : null}
              </div>
              <div className="text-gray-700">
                Elo пара:{' '}
                <span className={`font-medium ${
                  player.doublesEloApplyDisabled
                    ? 'bg-yellow-100 text-red-700 px-1.5 py-0.5 rounded'
                    : 'text-gray-900'
                }`}>
                  {doublesElo ? `${doublesElo.currentRating}` : '—'}
                </span>
                {doublesElo ? (
                  <span className={`ml-2 ${
                    doublesElo.lastDelta > 0 ? 'text-green-600' : doublesElo.lastDelta < 0 ? 'text-red-600' : 'text-gray-900'
                  }`}>
                    {doublesElo.lastDelta >= 0 ? '+' : ''}
                    {doublesElo.lastDelta}
                  </span>
                ) : null}
                {doublesElo ? (
                  <span className="ml-2 text-gray-500">игр: {doublesElo.matchesPlayed}</span>
                ) : null}
              </div>
            </div>

            {player.notes && (
              <div className="mb-4">
                <p className="text-sm text-gray-600">{player.notes}</p>
              </div>
            )}

            <div className="flex justify-between items-center">
              <span className="text-xs text-gray-500">
                Турниров: {player.memberships?.length || 0} • Матчей: {player.matchesCount || 0}
                {' • '}
                <button
                  type="button"
                  className={`text-xs underline ${
                    hasUnpostedDocuments
                      ? 'text-amber-700 hover:text-amber-800 font-semibold'
                      : 'text-blue-600 hover:text-blue-800'
                  }`}
                  onClick={() => openPlayerLevelDocuments(player)}
                >
                  Документов: {player.documentsCount || 0}
                </button>
                {player.tennisProfileLink?.platformIdentityId ? (
                  <>
                    {' • '}
                    <Link
                      className="text-xs text-indigo-600 hover:text-indigo-800 underline"
                      to={`/identities/${player.tennisProfileLink.platformIdentityId}`}
                    >
                      Identity →
                    </Link>
                  </>
                ) : null}
              </span>
              <div className="flex space-x-2">
                <button
                  onClick={() => handleEdit(player)}
                  className="text-blue-600 hover:text-blue-800 text-sm"
                >
                  Редактировать
                </button>
                {((player.memberships?.length || 0) === 0 && (player.settlements?.length || 0) === 0) && (
                  <button
                    onClick={() => handleDeleteClick(player)}
                    className="text-red-600 hover:text-red-800 text-sm"
                  >
                    Удалить
                  </button>
                )}
              </div>
            </div>
          </div>
          );
        })}
      </div>

      {players.length === 0 && (
        <div className="text-center py-12">
          <p className="text-gray-500">Игроки не найдены</p>
        </div>
      )}

      {/* Пагинация */}
      {pagination.pages > 1 && (
        <div className="flex justify-center items-center space-x-2 mt-8">
          <button
            onClick={() => handlePageChange(pagination.page - 1)}
            disabled={pagination.page === 1}
            className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Назад
          </button>
          
          <div className="flex space-x-1">
            {Array.from({ length: Math.min(5, pagination.pages) }, (_, i) => {
              const pageNum = Math.max(1, pagination.page - 2) + i;
              if (pageNum > pagination.pages) return null;
              
              return (
                <button
                  key={pageNum}
                  onClick={() => handlePageChange(pageNum)}
                  className={`px-3 py-2 text-sm font-medium rounded-md ${
                    pageNum === pagination.page
                      ? 'bg-blue-600 text-white'
                      : 'text-gray-500 bg-white border border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  {pageNum}
                </button>
              );
            })}
          </div>
          
          <button
            onClick={() => handlePageChange(pagination.page + 1)}
            disabled={pagination.page === pagination.pages}
            className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Вперед
          </button>
        </div>
      )}

      {/* Информация о пагинации */}
      {pagination.total > 0 && (
        <div className="text-center text-sm text-gray-500 mt-4">
          Показано {players.length} из {pagination.total} игроков
        </div>
      )}

      {/* Форма создания/редактирования */}
      {showForm && (
        <PlayerForm
          player={editingPlayer}
          onSave={handleFormSave}
          onCancel={handleFormCancel}
          defaultSinglesLevel={lastSelectedSinglesLevel}
          defaultDoublesLevel={lastSelectedDoublesLevel}
        />
      )}

      {showEloBackfillModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 p-4">
          <div className="w-full max-w-4xl max-h-[90vh] overflow-hidden bg-white rounded-lg shadow-xl">
            <div className="px-6 py-4 border-b flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">
                Начальные Elo остатки на 01.01.2022 (Одиночка)
              </h2>
              <button
                onClick={() => {
                  setShowEloBackfillModal(false);
                  setEloBackfillProgress(null);
                }}
                className="text-gray-500 hover:text-gray-700"
                disabled={savingEloBackfill}
              >
                Закрыть
              </button>
            </div>
            <div className="px-6 pt-3 pb-2 border-b">
              <div className="p-3 rounded-lg border border-blue-100 bg-blue-50">
                <div className="text-sm font-medium text-blue-900 mb-2">Легенда стартовых Elo (одиночка)</div>
                <div className="flex flex-wrap gap-2">
                  {SINGLES_ELO_START_LEGEND.map((item) => (
                    <span
                      key={item.level}
                      className="inline-flex items-center rounded-md bg-white border border-blue-200 px-2 py-1 text-xs text-blue-900"
                    >
                      {item.level}: {item.rating}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            <div className="p-6 overflow-auto max-h-[65vh]">
              {loadingEloBackfill ? (
                <div className="text-sm text-gray-600">Загрузка списка игроков...</div>
              ) : (
                <>
                  <p className="text-sm text-gray-600 mb-4">
                    В списке только игроки, у которых есть минимум один одиночный завершенный матч с 01.01.2022.
                  </p>
                  <div className="space-y-2">
                    {eloBackfillPlayers.map((item) => (
                      <div
                        key={item.playerId}
                        className="grid grid-cols-12 gap-3 items-center border border-gray-200 rounded-lg p-3"
                      >
                        <div className="col-span-8">
                          <div className="text-sm font-medium text-gray-900">{item.playerName}</div>
                          <div className="text-xs text-gray-500">
                            Уровень: {item.singlesLevel || '—'} • Elo игр: {item.eloMatchesPlayed}
                          </div>
                        </div>
                        <div className="col-span-2 text-sm text-gray-600">
                          Начальный Elo: {item.suggestedRating}
                        </div>
                        <div className="col-span-2">
                          <input
                            type="number"
                            min={1}
                            value={eloBackfillRatings[item.playerId] || ''}
                            onChange={(e) =>
                              setEloBackfillRatings((prev) => ({
                                ...prev,
                                [item.playerId]: e.target.value
                              }))
                            }
                            className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-sm"
                            disabled={savingEloBackfill}
                          />
                        </div>
                      </div>
                    ))}
                    {eloBackfillPlayers.length < 1 && (
                      <div className="text-sm text-gray-500">Подходящие игроки не найдены.</div>
                    )}
                  </div>
                </>
              )}
            </div>

            <div className="px-6 py-4 border-t space-y-3">
              {savingEloBackfill && (
                <div className="rounded-lg border border-blue-100 bg-blue-50 p-3">
                  <div className="flex items-center justify-between text-xs text-blue-900 mb-1">
                    <span>
                      Прогресс пересчета: {eloBackfillProgress?.handledMatches || 0}/{eloBackfillProgress?.totalMatches || 0} матчей ({eloBackfillProgress?.progressPercent || 0}%)
                    </span>
                    <span>
                      Прошло: {formatDuration(eloBackfillProgress?.elapsedMs)} • Осталось: {formatDuration(eloBackfillProgress?.etaMs)}
                    </span>
                  </div>
                  <div className="h-2 w-full rounded bg-blue-100 overflow-hidden">
                    <div
                      className="h-full bg-blue-600 transition-all duration-300"
                      style={{ width: `${Math.max(0, Math.min(100, eloBackfillProgress?.progressPercent || 0))}%` }}
                    />
                  </div>
                </div>
              )}

              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                    <input
                      type="checkbox"
                      checked={recalculateEloFrom2022}
                      onChange={(e) => setRecalculateEloFrom2022(e.target.checked)}
                      disabled={savingEloBackfill}
                    />
                    Пересчитать Elo-историю с 01.01.2022
                  </label>
                  <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                    K:
                    <input
                      type="number"
                      min={1}
                      max={128}
                      value={eloKFactor}
                      onChange={(e) => setEloKFactor(e.target.value)}
                      disabled={savingEloBackfill}
                      className="w-20 border border-gray-300 rounded-lg px-2 py-1.5 text-sm"
                    />
                  </label>
                </div>
                <button
                  onClick={handleEloBackfillSave}
                  disabled={savingEloBackfill || loadingEloBackfill}
                  className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  {savingEloBackfill ? 'Пересчет...' : 'Сохранить'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showLevelDocsModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 p-4">
          <div className="w-full max-w-3xl max-h-[90vh] overflow-hidden bg-white rounded-lg shadow-xl">
            <div className="px-6 py-4 border-b flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">
                Документы игрока: {levelDocsPlayerName}
              </h2>
              <div className="flex items-center gap-3">
                {levelDocsTab === 'levels' && (
                  <button
                    onClick={openCreateLevelDocument}
                    disabled={!levelDocsPlayerId}
                    className="text-blue-600 hover:text-blue-800 disabled:text-gray-400 text-sm"
                  >
                    Создать
                  </button>
                )}
                {levelDocsTab === 'elo' && (
                  <button
                    onClick={recalculateSelectedPlayerElo}
                    disabled={!levelDocsPlayerId || levelEloRecalculating}
                    className="text-blue-600 hover:text-blue-800 disabled:text-gray-400 text-sm"
                  >
                    {levelEloRecalculating ? 'Пересчет...' : 'Пересчет'}
                  </button>
                )}
                <button
                  onClick={() => loadPlayerLevelDocuments(levelDocsPlayerId, { isRefresh: true })}
                  disabled={levelDocsRefreshing || levelDocsLoading || levelEloRecalculating}
                  className="text-blue-600 hover:text-blue-800 disabled:text-gray-400 text-sm"
                >
                  {levelDocsRefreshing ? 'Обновление...' : 'Обновить'}
                </button>
                <button
                  onClick={() => setShowLevelDocsModal(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  Закрыть
                </button>
              </div>
            </div>
            <div className="p-6 overflow-auto max-h-[75vh]">
              <div className="mb-4 border-b border-gray-200">
                <div className="flex items-center gap-4">
                  <button
                    type="button"
                    onClick={() => setLevelDocsTab('levels')}
                    className={`pb-2 text-sm border-b-2 transition ${
                      levelDocsTab === 'levels'
                        ? 'border-blue-600 text-blue-700 font-medium'
                        : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    Уровни
                  </button>
                  <button
                    type="button"
                    onClick={() => setLevelDocsTab('elo')}
                    className={`pb-2 text-sm border-b-2 transition ${
                      levelDocsTab === 'elo'
                        ? 'border-blue-600 text-blue-700 font-medium'
                        : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    Elo
                  </button>
                </div>
              </div>
              {levelDocsLoading ? (
                <div className="text-sm text-gray-600">Загрузка документов...</div>
              ) : levelDocsTab === 'levels' ? (
                levelDocs.length === 0 ? (
                  <div className="text-sm text-gray-500">Документы не найдены</div>
                ) : (
                <div className="space-y-3">
                  {levelDocs.map((doc) => (
                    <div key={doc.id} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-2">
                        <button
                          type="button"
                          onClick={() => window.open(`/documents/player-level-changes?documentId=${doc.id}&mode=edit`, '_blank', 'noopener,noreferrer')}
                          className="text-sm font-medium text-blue-700 hover:text-blue-900 underline text-left"
                        >
                          {doc.documentType === 'INITIAL_ASSIGNMENT' ? 'Установка начального уровня' : 'Изменение уровня'}
                        </button>
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            title={String(doc.status || '').toUpperCase() === 'POSTED' ? 'Перепровести' : 'Провести'}
                            onClick={() => handleLevelDocStatusChange(doc.id, 'POSTED')}
                            disabled={levelDocActionLoadingId === doc.id}
                            className="text-green-700 hover:text-green-900 disabled:text-gray-400 text-sm"
                          >
                            ✅
                          </button>
                          {String(doc.status || '').toUpperCase() === 'POSTED' && (
                            <button
                              type="button"
                              title="Снять с проведения"
                              onClick={() => handleLevelDocStatusChange(doc.id, 'DRAFT')}
                              disabled={levelDocActionLoadingId === doc.id}
                              className="text-amber-700 hover:text-amber-900 disabled:text-gray-400 text-sm"
                            >
                              ↩️
                            </button>
                          )}
                          {String(doc.status || '').toUpperCase() === 'DRAFT' && (
                            <button
                              type="button"
                              title="Удалить"
                              onClick={() => handleLevelDocDelete(doc.id)}
                              disabled={levelDocActionLoadingId === doc.id}
                              className="text-red-700 hover:text-red-900 disabled:text-gray-400 text-sm"
                            >
                              🗑️
                            </button>
                          )}
                          <div className="text-xs text-gray-500">
                            {doc.status} • {new Date(doc.effectiveDate).toLocaleDateString('ru-RU')}
                          </div>
                        </div>
                      </div>
                      <div className="space-y-1">
                        {doc.items.map((item) => (
                          <div key={item.id} className="text-xs text-gray-700 flex items-center justify-between gap-3">
                            <span>{item.levelType}: {item.previousLevel || '—'} → {item.newLevel}</span>
                            <span className={`whitespace-nowrap px-1.5 py-0.5 rounded ${
                              item.applyElo === true
                                ? 'text-gray-500'
                                : 'bg-yellow-100 text-red-700 font-semibold'
                            }`}>
                              Elo: {typeof item.eloAtEffectiveDate === 'number' ? item.eloAtEffectiveDate : '—'}
                            </span>
                          </div>
                        ))}
                      </div>
                      {doc.notes && (
                        <div className="mt-2 text-xs text-gray-500">{doc.notes}</div>
                      )}
                    </div>
                  ))}
                </div>
                )
              ) : eloMatchHistory.length === 0 ? (
                <div className="text-sm text-gray-500">Elo-матчи не найдены</div>
              ) : (
                <div className="space-y-3">
                  {eloChartData && (
                    <div className="border border-gray-200 rounded-lg p-3 bg-white">
                      <div className="flex items-center justify-between gap-3 text-xs text-gray-600 mb-2">
                        <div className="flex items-center gap-3">
                          <div className="inline-flex rounded-md border border-gray-300 overflow-hidden">
                            <button
                              type="button"
                              onClick={() => setEloChartDiscipline('SINGLES')}
                              className={`px-2 py-0.5 text-xs ${
                                eloChartDiscipline === 'SINGLES'
                                  ? 'bg-blue-600 text-white'
                                  : 'bg-white text-gray-700 hover:bg-gray-50'
                              }`}
                            >
                              S
                            </button>
                            <button
                              type="button"
                              onClick={() => setEloChartDiscipline('DOUBLES')}
                              className={`px-2 py-0.5 text-xs border-l border-gray-300 ${
                                eloChartDiscipline === 'DOUBLES'
                                  ? 'bg-blue-600 text-white'
                                  : 'bg-white text-gray-700 hover:bg-gray-50'
                              }`}
                            >
                              D
                            </button>
                          </div>
                          <span className="font-medium text-gray-700">
                            {eloChartDiscipline === 'SINGLES' ? 'Одиночный Elo' : 'Парный Elo'}
                          </span>
                        </div>
                        <span>
                          Elo: {eloChartData.minRating} - {eloChartData.maxRating}
                        </span>
                        <span>
                          Текущий Elo: {eloChartData.currentRating ?? '—'}
                        </span>
                      </div>
                      <svg
                        viewBox={`0 0 ${eloChartData.width} ${eloChartData.height}`}
                        className="w-full h-auto"
                        style={{ aspectRatio: `${eloChartData.width} / ${eloChartData.height}` }}
                      >
                        <line
                          x1={eloChartData.padLeft}
                          y1={eloChartData.padTop}
                          x2={eloChartData.padLeft}
                          y2={eloChartData.height - eloChartData.padBottom}
                          stroke="#d1d5db"
                          strokeWidth={1}
                        />
                        <line
                          x1={eloChartData.padLeft}
                          y1={eloChartData.height - eloChartData.padBottom}
                          x2={eloChartData.plotRight}
                          y2={eloChartData.height - eloChartData.padBottom}
                          stroke="#d1d5db"
                          strokeWidth={1}
                        />
                        <line
                          x1={eloChartData.padLeft}
                          y1={eloChartData.padTop}
                          x2={eloChartData.plotRight}
                          y2={eloChartData.padTop}
                          stroke="#f3f4f6"
                          strokeWidth={1}
                        />
                        <line
                          x1={eloChartData.padLeft}
                          y1={(eloChartData.padTop + (eloChartData.height - eloChartData.padBottom)) / 2}
                          x2={eloChartData.plotRight}
                          y2={(eloChartData.padTop + (eloChartData.height - eloChartData.padBottom)) / 2}
                          stroke="#f3f4f6"
                          strokeWidth={1}
                        />
                        {eloChartData.levelGuides.map((guide) => (
                          <g key={guide.level}>
                            <line
                              x1={eloChartData.padLeft}
                              y1={guide.y}
                              x2={eloChartData.plotRight}
                              y2={guide.y}
                              stroke="#9ca3af"
                              strokeWidth={1}
                              strokeDasharray="4 4"
                              opacity={0.75}
                            />
                            <text
                              x={eloChartData.plotRight + 8}
                              y={guide.y - 2}
                              fontSize={10}
                              fill="#6b7280"
                              textAnchor="start"
                            >
                              {guide.level} ({guide.rating})
                            </text>
                          </g>
                        ))}
                        <path d={eloChartData.linePath} fill="none" stroke="#2563eb" strokeWidth={2} />
                        {eloChartData.points.map((point) => (
                          <g
                            key={point.id}
                            className="cursor-pointer"
                            onMouseEnter={() => setEloHoveredPointId(point.id)}
                            onMouseLeave={() => setEloHoveredPointId(null)}
                          >
                            <circle
                              cx={point.x}
                              cy={point.y}
                              r={8}
                              fill="transparent"
                            >
                              <title>{`${point.label} • Elo: ${point.rating}`}</title>
                            </circle>
                            {point.isLevelChange && (
                              <circle
                                cx={point.x}
                                cy={point.y}
                                r={eloHoveredPointId === point.id ? 6 : 5}
                                fill="none"
                                stroke="#dc2626"
                                strokeWidth={2}
                                pointerEvents="none"
                              />
                            )}
                            <circle
                              cx={point.x}
                              cy={point.y}
                              r={eloHoveredPointId === point.id ? 4 : 3}
                              fill="#1d4ed8"
                              pointerEvents="none"
                            />
                          </g>
                        ))}
                        {(() => {
                          const hoveredPoint = eloChartData.points.find((point) => point.id === eloHoveredPointId);
                          if (!hoveredPoint) return null;
                          const tooltipWidth = 136;
                          const tooltipHeight = 42;
                          const minX = eloChartData.padLeft;
                          const maxX = eloChartData.plotRight - tooltipWidth;
                          const tooltipX = Math.min(maxX, Math.max(minX, hoveredPoint.x - tooltipWidth / 2));
                          const tooltipY = hoveredPoint.y <= eloChartData.padTop + tooltipHeight
                            ? hoveredPoint.y + 10
                            : hoveredPoint.y - tooltipHeight - 10;
                          return (
                            <g pointerEvents="none">
                              <rect
                                x={tooltipX}
                                y={tooltipY}
                                width={tooltipWidth}
                                height={tooltipHeight}
                                rx={6}
                                fill="#111827"
                                fillOpacity={0.94}
                              />
                              <text x={tooltipX + 8} y={tooltipY + 16} fontSize={11} fill="#f9fafb">
                                {hoveredPoint.label}
                              </text>
                              <text x={tooltipX + 8} y={tooltipY + 32} fontSize={11} fill="#93c5fd">
                                Elo: {hoveredPoint.rating}
                              </text>
                            </g>
                          );
                        })()}
                        <text
                          x={4}
                          y={eloChartData.padTop + 4}
                          fontSize={11}
                          fill="#6b7280"
                        >
                          {eloChartData.maxScaleRating}
                        </text>
                        <text
                          x={4}
                          y={eloChartData.height - eloChartData.padBottom + 4}
                          fontSize={11}
                          fill="#6b7280"
                        >
                          {eloChartData.minScaleRating}
                        </text>
                        <text
                          x={eloChartData.padLeft}
                          y={eloChartData.height - 8}
                          fontSize={11}
                          fill="#6b7280"
                        >
                          {eloChartData.firstLabel}
                        </text>
                        <text
                          x={eloChartData.plotRight}
                          y={eloChartData.height - 8}
                          fontSize={11}
                          fill="#6b7280"
                          textAnchor="end"
                        >
                          {eloChartData.lastLabel}
                        </text>
                      </svg>
                    </div>
                  )}
                  {!eloChartData && (
                    <div className="border border-gray-200 rounded-lg p-3 bg-white">
                      <div className="flex items-center gap-3 text-xs text-gray-600 mb-2">
                        <div className="inline-flex rounded-md border border-gray-300 overflow-hidden">
                          <button
                            type="button"
                            onClick={() => setEloChartDiscipline('SINGLES')}
                            className={`px-2 py-0.5 text-xs ${
                              eloChartDiscipline === 'SINGLES'
                                ? 'bg-blue-600 text-white'
                                : 'bg-white text-gray-700 hover:bg-gray-50'
                            }`}
                          >
                            S
                          </button>
                          <button
                            type="button"
                            onClick={() => setEloChartDiscipline('DOUBLES')}
                            className={`px-2 py-0.5 text-xs border-l border-gray-300 ${
                              eloChartDiscipline === 'DOUBLES'
                                ? 'bg-blue-600 text-white'
                                : 'bg-white text-gray-700 hover:bg-gray-50'
                            }`}
                          >
                            D
                          </button>
                        </div>
                        <span className="font-medium text-gray-700">
                          {eloChartDiscipline === 'SINGLES' ? 'Одиночный Elo' : 'Парный Elo'}
                        </span>
                      </div>
                      <div className="text-xs text-gray-500">
                        Для выбранной дисциплины пока нет данных для построения графика.
                      </div>
                    </div>
                  )}
                  {filteredEloHistoryRows.length === 0 ? (
                    <div className="text-xs text-gray-500">
                      Для выбранной дисциплины пока нет событий в истории.
                    </div>
                  ) : filteredEloHistoryRows.map((row) => {
                    const displayDate = row.eventDate || row.matchDate || row.createdAt;
                    const isLevelDocumentEvent = String(row.eventType || '').toUpperCase() === 'LEVEL_DOCUMENT';
                    const hasDelta = typeof row.delta === 'number';
                    return (
                      <div key={row.id} className="border border-gray-200 rounded-lg px-3 py-1.5">
                        <div className="flex items-center justify-between gap-3">
                          <div className="text-xs text-gray-500">
                            {new Date(displayDate).toLocaleString('ru-RU')} • {row.discipline}
                            {isLevelDocumentEvent
                              ? ` • ${String(row.documentType || '').toUpperCase() === 'INITIAL_ASSIGNMENT' ? 'Установка уровня' : 'Изменение уровня'}`
                              : ''}
                          </div>
                          <div className={`text-sm font-medium ${
                            isLevelDocumentEvent
                              ? 'text-blue-700'
                              : hasDelta
                                ? (row.delta || 0) > 0
                                  ? 'text-green-700'
                                  : (row.delta || 0) < 0
                                    ? 'text-red-700'
                                    : 'text-gray-900'
                                : 'text-gray-700'
                          }`}>
                            {typeof row.ratingBefore === 'number' && typeof row.ratingAfter === 'number'
                              ? `${row.ratingBefore} → ${row.ratingAfter}${hasDelta ? ` (${(row.delta || 0) >= 0 ? '+' : ''}${row.delta})` : ''}`
                              : `Elo: ${typeof row.ratingAfter === 'number' ? row.ratingAfter : '—'}`}
                          </div>
                        </div>
                        {isLevelDocumentEvent ? (
                          <div className="mt-0.5 text-xs text-gray-700">
                            Уровень: {row.levelBefore || '—'} → {row.levelAfter || '—'}{row.status ? ` • ${row.status}` : ''}
                          </div>
                        ) : (
                          <>
                            <div className="mt-0.5 text-xs text-gray-700">
                              {row.tournamentName || 'Турнир'}
                              {row.level ? ` • ${row.level}` : ''}
                              {row.stage ? ` • ${row.stage}` : ''}
                              {row.opponentName ? ` • vs ${row.opponentName}` : ''}
                              {typeof row.opponentRatingBefore === 'number' ? ` • Elo: ${row.opponentRatingBefore}` : ''}
                            </div>
                            <div className="mt-0.5 text-xs text-gray-500">
                              {row.score ? `Счет: ${row.score}` : 'Счет: —'}{row.status ? ` • ${row.status}` : ''}
                            </div>
                          </>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {showLevelHistoryModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 p-4">
          <div className="w-full max-w-2xl max-h-[90vh] overflow-hidden bg-white rounded-lg shadow-xl">
            <div className="px-6 py-4 border-b flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">
                История уровней: {levelHistoryPlayerName}
              </h2>
              <button
                onClick={() => setShowLevelHistoryModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                Закрыть
              </button>
            </div>
            <div className="p-6 overflow-auto max-h-[75vh]">
              {levelHistoryLoading ? (
                <div className="text-sm text-gray-600">Загрузка истории...</div>
              ) : levelHistoryRows.length === 0 ? (
                <div className="text-sm text-gray-500">История изменений уровней не найдена</div>
              ) : (
                <table className="min-w-full text-sm border border-gray-200 rounded-lg overflow-hidden">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="text-left px-3 py-2 border-b">Дата</th>
                      <th className="text-left px-3 py-2 border-b">Одиночка</th>
                      <th className="text-left px-3 py-2 border-b">Пара</th>
                    </tr>
                  </thead>
                  <tbody>
                    {levelHistoryRows.map((row) => (
                      <tr key={row.effectiveDate.toISOString()} className="odd:bg-white even:bg-gray-50">
                        <td className="px-3 py-2 border-b border-gray-100">{row.date}</td>
                        <td className="px-3 py-2 border-b border-gray-100">{row.singlesLevel || '—'}</td>
                        <td className="px-3 py-2 border-b border-gray-100">{row.doublesLevel || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Подтверждение удаления */}
      {showDeleteConfirm && playerToDelete && (
        <ConfirmDialog
          isOpen={showDeleteConfirm}
          title="Удалить игрока"
          message={`Вы уверены, что хотите навсегда удалить игрока "${playerToDelete.name}"? Это действие нельзя отменить.`}
          confirmText="Удалить"
          cancelText="Отмена"
          onConfirm={handleDeleteConfirm}
          onCancel={handleDeleteCancel}
          type="danger"
        />
      )}

      {/* Подтверждение деактивации */}
      {showDeactivateConfirm && playerToDelete && (
        <ConfirmDialog
          isOpen={showDeactivateConfirm}
          title="Деактивировать игрока"
          message={`Вы уверены, что хотите деактивировать игрока "${playerToDelete.name}"? Игрок будет помечен как неактивный, но его данные сохранятся.`}
          confirmText="Деактивировать"
          cancelText="Отмена"
          onConfirm={handleDeactivateConfirm}
          onCancel={handleDeactivateCancel}
          type="warning"
        />
      )}
    </div>
  );
};

// Компонент формы игрока
const PlayerForm: React.FC<{
  player: Player | null;
  onSave: (data: Partial<Player>) => void;
  onCancel: () => void;
  defaultSinglesLevel?: string;
  defaultDoublesLevel?: string;
}> = ({ player, onSave, onCancel, defaultSinglesLevel = 'S-6', defaultDoublesLevel = 'D-6' }) => {
  const [formData, setFormData] = useState({
    name: player?.name || '',
    websiteName: player?.websiteName || '',
    email: player?.email || '',
    phone: player?.phone || '',
    telegramChatId: player?.telegramChatId || '', // Устаревшее, для обратной совместимости
    telegramUsername: player?.telegramUsername || '', // Устаревшее, для обратной совместимости
    // Для редактирования показываем фактические значения из БД (включая пустые),
    // дефолты применяем только при создании нового игрока.
    singlesLevel: player ? (player.singlesLevel ?? '') : defaultSinglesLevel,
    doublesLevel: player ? (player.doublesLevel ?? '') : defaultDoublesLevel,
    notes: player?.notes || '',
    isActive: player?.isActive ?? true,
    excludeFromWebsite: player?.excludeFromWebsite ?? false
  });
  
  // Обновляем formData при изменении player
  useEffect(() => {
    if (player) {
      setFormData({
        name: player.name || '',
        websiteName: player.websiteName || '',
        email: player.email || '',
        phone: player.phone || '',
        telegramChatId: player.telegramChatId || '',
        telegramUsername: player.telegramUsername || '',
        singlesLevel: player.singlesLevel ?? '',
        doublesLevel: player.doublesLevel ?? '',
        notes: player.notes || '',
        isActive: player.isActive ?? true,
        excludeFromWebsite: player.excludeFromWebsite ?? false
      });
    }
  }, [player, defaultSinglesLevel, defaultDoublesLevel]);
  
  const [telegramAccounts, setTelegramAccounts] = useState<PlayerTelegramAccount[]>([]);
  const [loadingAccounts, setLoadingAccounts] = useState(false);
  const [showAccountForm, setShowAccountForm] = useState(false);
  const [editingAccount, setEditingAccount] = useState<PlayerTelegramAccount | null>(null);
  const [accountFormData, setAccountFormData] = useState({
    chatId: '',
    username: '',
    firstName: '',
    lastName: '',
    isPrimary: false
  });

  const singlesLevels = [
    { value: '', label: 'Не указан' },
    { value: 'S-4F', label: 'S-4F (4-й женский)' },
    { value: 'S-4M', label: 'S-4M (4-й мужской)' },
    { value: 'S-5', label: 'S-5 (5-й)' },
    { value: 'S-6', label: 'S-6 (6-й)' },
    { value: 'S-7', label: 'S-7 (7-й)' },
    { value: 'S-8', label: 'S-8 (8-й)' },
    { value: 'S-9', label: 'S-9 (9-й)' }
  ];

  const doublesLevels = [
    { value: '', label: 'Не указан' },
    { value: 'D-Mixt', label: 'D-Mixt (Смешанная)' },
    { value: 'D-6', label: 'D-6 (6-й)' },
    { value: 'D-6.5', label: 'D-6.5 (6.5-й)' },
    { value: 'D-7', label: 'D-7 (7-й)' },
    { value: 'D-ProAm', label: 'D-ProAm (Про-Ам)' }
  ];

  // Загружаем Telegram аккаунты при редактировании игрока
  useEffect(() => {
    if (player?.id) {
      loadTelegramAccounts(player.id);
    } else {
      setTelegramAccounts([]);
    }
  }, [player?.id]);

  const loadTelegramAccounts = async (playerId: string) => {
    try {
      setLoadingAccounts(true);
      const data = await apiGet<{ accounts: PlayerTelegramAccount[] }>(`/players/${playerId}/telegram-accounts`);
      setTelegramAccounts(data.accounts || []);
    } catch (error) {
      console.error('Error loading telegram accounts:', error);
    } finally {
      setLoadingAccounts(false);
    }
  };

  const handleAddAccount = () => {
    setEditingAccount(null);
    setAccountFormData({
      chatId: '',
      username: '',
      firstName: '',
      lastName: '',
      isPrimary: telegramAccounts.length === 0
    });
    setShowAccountForm(true);
  };

  const handleEditAccount = (account: PlayerTelegramAccount) => {
    setEditingAccount(account);
    setAccountFormData({
      chatId: account.chatId,
      username: account.username || '',
      firstName: account.firstName || '',
      lastName: account.lastName || '',
      isPrimary: account.isPrimary
    });
    setShowAccountForm(true);
  };

  const handleSaveAccount = async () => {
    if (!player?.id) return;
    
    if (!accountFormData.chatId.trim()) {
      alert('Chat ID обязателен');
      return;
    }

    try {
      // Проверяем, редактируем ли мы старый аккаунт
      const isEditingOldAccount = (window as any).editingOldTelegramAccount === true;
      
      if (isEditingOldAccount) {
        // Обновляем старые поля в таблице Player
        const updatedTelegramChatId = accountFormData.chatId.trim();
        const updatedTelegramUsername = accountFormData.username.trim() 
          ? (accountFormData.username.trim().startsWith('@') 
              ? accountFormData.username.trim() 
              : `@${accountFormData.username.trim()}`)
          : '';
        
        // Обновляем локальное состояние формы
        setFormData({
          ...formData,
          telegramChatId: updatedTelegramChatId,
          telegramUsername: updatedTelegramUsername
        });
        
        // Сохраняем игрока с обновленными полями
        const submitData: Partial<Player> = {
          ...formData,
          telegramChatId: updatedTelegramChatId || undefined,
          telegramUsername: updatedTelegramUsername || undefined,
          email: formData.email?.trim() || undefined,
          phone: formData.phone?.trim() || undefined,
          notes: formData.notes?.trim() || undefined,
          singlesLevel: formData.singlesLevel?.trim() ? formData.singlesLevel.trim() : null,
          doublesLevel: formData.doublesLevel?.trim() ? formData.doublesLevel.trim() : null
        };
        
        // Вызываем onSave для сохранения изменений
        await onSave(submitData);
        
        // Сбрасываем флаг
        (window as any).editingOldTelegramAccount = false;
        
        // Закрываем форму аккаунта
        setShowAccountForm(false);
        setEditingAccount(null);
        
        // Обновляем форму игрока, чтобы отобразить изменения
        // (onSave обновит editingPlayer через handleFormSave)
        return; // Выходим, чтобы не выполнять код ниже
      } else {
        // Работаем с новой таблицей PlayerTelegramAccount
        if (editingAccount) {
          await apiPut(`/players/${player.id}/telegram-accounts/${editingAccount.id}`, accountFormData);
        } else {
          await apiPost(`/players/${player.id}/telegram-accounts`, accountFormData);
        }
        await loadTelegramAccounts(player.id);
      }
      
      setShowAccountForm(false);
      setEditingAccount(null);
    } catch (error: any) {
      console.error('Error saving telegram account:', error);
      alert(error.response?.data?.error || 'Ошибка при сохранении аккаунта');
    }
  };

  const handleDeleteAccount = async (accountId: string) => {
    if (!player?.id) return;
    
    if (!confirm('Вы уверены, что хотите удалить этот Telegram аккаунт?')) {
      return;
    }

    try {
      await apiDelete(`/players/${player.id}/telegram-accounts/${accountId}`);
      await loadTelegramAccounts(player.id);
    } catch (error: any) {
      console.error('Error deleting telegram account:', error);
      alert(error.response?.data?.error || 'Ошибка при удалении аккаунта');
    }
  };

  // Обновляем значения по умолчанию при изменении пропсов
  // Используем флаг, чтобы не перезаписывать изменения пользователя
  const [isFormDataModified, setIsFormDataModified] = useState(false);
  const [lastSavedPlayerId, setLastSavedPlayerId] = useState<string | null>(null);
  
  useEffect(() => {
    if (player && !isFormDataModified) {
      // При редактировании игрока загружаем все данные
      setFormData({
        name: player.name || '',
        websiteName: player.websiteName || '',
        email: player.email || '',
        phone: player.phone || '',
        telegramChatId: player.telegramChatId || '',
        telegramUsername: player.telegramUsername || '',
        singlesLevel: player.singlesLevel ?? '',
        doublesLevel: player.doublesLevel ?? '',
        notes: player.notes || '',
        isActive: player.isActive ?? true,
        excludeFromWebsite: player.excludeFromWebsite ?? false
      });
      // Если это новый player (после сохранения), сбрасываем флаг
      if (lastSavedPlayerId === player.id) {
        setIsFormDataModified(false);
        setLastSavedPlayerId(null);
      }
    } else if (!player) {
      // При создании нового игрока используем значения по умолчанию
      setFormData(prev => ({
        ...prev,
        name: '',
        websiteName: '',
        email: '',
        phone: '',
        telegramChatId: '',
        telegramUsername: '',
        notes: '',
        singlesLevel: defaultSinglesLevel,
        doublesLevel: defaultDoublesLevel,
        isActive: true,
        excludeFromWebsite: false
      }));
      setIsFormDataModified(false);
      setLastSavedPlayerId(null);
    }
  }, [defaultSinglesLevel, defaultDoublesLevel, player, isFormDataModified, lastSavedPlayerId]);
  
  // Сбрасываем флаг при изменении player.id
  useEffect(() => {
    if (player?.id && player.id !== lastSavedPlayerId) {
      setIsFormDataModified(false);
    }
  }, [player?.id, lastSavedPlayerId]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      alert('Имя обязательно для заполнения');
      return;
    }

    // Валидация email (если заполнен)
    if (formData.email.trim()) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) {
        alert('Пожалуйста, введите корректный email адрес');
        return;
      }
    }

    const submitData: Partial<Player> = {
      ...formData,
      // Преобразуем пустые строки в null для очистки полей в базе данных
      // ВАЖНО: Для telegramChatId и telegramUsername используем null, чтобы очистить поля
      telegramChatId: formData.telegramChatId?.trim() ? formData.telegramChatId.trim() : null,
      telegramUsername: formData.telegramUsername?.trim() ? formData.telegramUsername.trim() : null,
      websiteName: formData.websiteName?.trim() || undefined,
      email: formData.email?.trim() || undefined,
      phone: formData.phone?.trim() || undefined,
      notes: formData.notes?.trim() || undefined,
      // Явно отправляем null для очистки уровня в БД
      singlesLevel: formData.singlesLevel?.trim() ? formData.singlesLevel.trim() : null,
      doublesLevel: formData.doublesLevel?.trim() ? formData.doublesLevel.trim() : null
    };

    // Сохраняем ID игрока перед сохранением, чтобы после обновления editingPlayer
    // мы могли сбросить флаг isFormDataModified
    if (player?.id) {
      setLastSavedPlayerId(player.id);
    }
    onSave(submitData);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
        <h2 className="text-xl font-bold mb-4">
          {player ? 'Редактировать игрока' : 'Добавить игрока'}
        </h2>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Имя *
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Имя на сайте
            </label>
            <input
              type="text"
              value={formData.websiteName}
              onChange={(e) => setFormData({ ...formData, websiteName: e.target.value })}
              placeholder="Если пусто, используется основное имя"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email
            </label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Телефон
            </label>
            <input
              type="tel"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div className="border-t border-gray-200 pt-4 mt-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-gray-700">Telegram аккаунты</h3>
              {player?.id && (
                <button
                  type="button"
                  onClick={handleAddAccount}
                  className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                >
                  + Добавить аккаунт
                </button>
              )}
            </div>
            
            {player?.id && (
              <>
                {loadingAccounts ? (
                  <p className="text-sm text-gray-500">Загрузка...</p>
                ) : (
                  <>
                    {/* Старый формат (telegramChatId и telegramUsername из таблицы Player) */}
                    {(formData.telegramChatId || formData.telegramUsername) && (
                      <div className="space-y-2 mb-3">
                        <div className="flex items-center justify-between p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              {formData.telegramChatId && (
                                <span className="text-sm font-medium text-gray-900">
                                  Chat ID: {formData.telegramChatId}
                                </span>
                              )}
                              <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-0.5 rounded">
                                Старый формат
                              </span>
                            </div>
                            {formData.telegramUsername && (
                              <p className="text-xs text-gray-600 mt-1">
                                {formData.telegramUsername.startsWith('@') 
                                  ? formData.telegramUsername 
                                  : `@${formData.telegramUsername}`}
                              </p>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              onClick={() => {
                                // Редактирование старого аккаунта
                                setEditingAccount(null);
                                setAccountFormData({
                                  chatId: formData.telegramChatId || '',
                                  username: formData.telegramUsername?.replace('@', '') || '',
                                  firstName: '',
                                  lastName: '',
                                  isPrimary: telegramAccounts.length === 0
                                });
                                setShowAccountForm(true);
                                // Устанавливаем флаг, что это старый аккаунт
                                (window as any).editingOldTelegramAccount = true;
                              }}
                              className="text-sm text-blue-600 hover:text-blue-800"
                            >
                              Изменить
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                if (confirm('Удалить старую привязку Telegram? Это действие нельзя отменить.')) {
                                  setIsFormDataModified(true);
                                  setFormData({
                                    ...formData,
                                    telegramChatId: '',
                                    telegramUsername: ''
                                  });
                                }
                              }}
                              className="text-sm text-red-600 hover:text-red-800"
                            >
                              Удалить
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {/* Новый формат (PlayerTelegramAccount) */}
                    {telegramAccounts.length === 0 && !formData.telegramChatId && !formData.telegramUsername ? (
                      <p className="text-sm text-gray-500 mb-3">
                        Нет привязанных Telegram аккаунтов. Добавьте аккаунт, чтобы игрок мог получать уведомления.
                      </p>
                    ) : (
                      telegramAccounts.length > 0 && (
                        <div className="space-y-2 mb-3">
                          {telegramAccounts.map((account) => (
                            <div
                              key={account.id}
                              className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200"
                            >
                              <div className="flex-1">
                                <div className="flex items-center gap-2">
                                  <span className="text-sm font-medium text-gray-900">
                                    Chat ID: {account.chatId}
                                  </span>
                                  {account.isPrimary && (
                                    <span className="text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded">
                                      Основной
                                    </span>
                                  )}
                                </div>
                                {account.username && (
                                  <p className="text-xs text-gray-600 mt-1">@{account.username}</p>
                                )}
                                {(account.firstName || account.lastName) && (
                                  <p className="text-xs text-gray-600">
                                    {[account.firstName, account.lastName].filter(Boolean).join(' ')}
                                  </p>
                                )}
                              </div>
                              <div className="flex items-center gap-2">
                                <button
                                  type="button"
                                  onClick={() => {
                                    (window as any).editingOldTelegramAccount = false;
                                    handleEditAccount(account);
                                  }}
                                  className="text-sm text-blue-600 hover:text-blue-800"
                                >
                                  Изменить
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleDeleteAccount(account.id)}
                                  className="text-sm text-red-600 hover:text-red-800"
                                >
                                  Удалить
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )
                    )}
                  </>
                )}
              </>
            )}
            
            {!player?.id && (
              <p className="text-sm text-gray-500 mb-3">
                Сохраните игрока, чтобы добавить Telegram аккаунты.
              </p>
            )}
          </div>
          
          {/* Форма добавления/редактирования Telegram аккаунта */}
          {showAccountForm && player?.id && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
                <h3 className="text-lg font-bold mb-4">
                  {editingAccount ? 'Редактировать Telegram аккаунт' : 'Добавить Telegram аккаунт'}
                </h3>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Chat ID *
                    </label>
                    <input
                      type="text"
                      value={accountFormData.chatId}
                      onChange={(e) => setAccountFormData({ ...accountFormData, chatId: e.target.value })}
                      placeholder="123456789"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Username
                    </label>
                    <input
                      type="text"
                      value={accountFormData.username}
                      onChange={(e) => setAccountFormData({ ...accountFormData, username: e.target.value.replace('@', '') })}
                      placeholder="username (без @)"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Имя
                    </label>
                    <input
                      type="text"
                      value={accountFormData.firstName}
                      onChange={(e) => setAccountFormData({ ...accountFormData, firstName: e.target.value })}
                      placeholder="Имя из Telegram"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Фамилия
                    </label>
                    <input
                      type="text"
                      value={accountFormData.lastName}
                      onChange={(e) => setAccountFormData({ ...accountFormData, lastName: e.target.value })}
                      placeholder="Фамилия из Telegram"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="isPrimary"
                      checked={accountFormData.isPrimary}
                      onChange={(e) => setAccountFormData({ ...accountFormData, isPrimary: e.target.checked })}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label htmlFor="isPrimary" className="ml-2 block text-sm text-gray-700">
                      Основной аккаунт
                    </label>
                  </div>
                </div>
                
                <div className="flex justify-end gap-3 mt-6">
                  <button
                    type="button"
                    onClick={() => {
                      setShowAccountForm(false);
                      setEditingAccount(null);
                      (window as any).editingOldTelegramAccount = false;
                    }}
                    className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300"
                  >
                    Отмена
                  </button>
                  <button
                    type="button"
                    onClick={handleSaveAccount}
                    className="px-4 py-2 text-white bg-blue-600 rounded-lg hover:bg-blue-700"
                  >
                    Сохранить
                  </button>
                </div>
              </div>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Уровень в одиночке
            </label>
            <select
              value={formData.singlesLevel}
              onChange={(e) => setFormData({ ...formData, singlesLevel: e.target.value })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {singlesLevels.map(level => (
                <option key={level.value} value={level.value}>
                  {level.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Уровень в паре
            </label>
            <select
              value={formData.doublesLevel}
              onChange={(e) => setFormData({ ...formData, doublesLevel: e.target.value })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {doublesLevels.map(level => (
                <option key={level.value} value={level.value}>
                  {level.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Заметки
            </label>
            <input
              type="text"
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div className="flex items-center gap-6">
            <label htmlFor="isActive" className="flex items-center text-sm text-gray-700">
              <input
                type="checkbox"
                id="isActive"
                checked={formData.isActive}
                onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <span className="ml-2">Активный игрок</span>
            </label>
            <label htmlFor="excludeFromWebsite" className="flex items-center text-sm text-gray-700">
              <input
                type="checkbox"
                id="excludeFromWebsite"
                checked={formData.excludeFromWebsite}
                onChange={(e) => setFormData({ ...formData, excludeFromWebsite: e.target.checked })}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <span className="ml-2">Не передавать на сайт</span>
            </label>
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onCancel}
              className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300 transition-colors"
            >
              Отмена
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              {player ? 'Сохранить' : 'Создать'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Компонент подтверждения удаления
const ConfirmDialog: React.FC<{
  isOpen: boolean;
  title: string;
  message: string;
  confirmText: string;
  cancelText: string;
  onConfirm: () => void;
  onCancel: () => void;
  type?: 'danger' | 'warning' | 'info';
}> = ({ isOpen, title, message, confirmText, cancelText, onConfirm, onCancel, type = 'danger' }) => {
  if (!isOpen) return null;

  const buttonColor = type === 'danger' ? 'bg-red-600 hover:bg-red-700' : 'bg-blue-600 hover:bg-blue-700';

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">{title}</h3>
        <p className="text-gray-600 mb-6">{message}</p>
        <div className="flex justify-end space-x-3">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300 transition-colors"
          >
            {cancelText}
          </button>
          <button
            onClick={onConfirm}
            className={`px-4 py-2 text-white rounded-lg transition-colors ${buttonColor}`}
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
};

export default Players;
